import api from "../../services/api";
import type {
  GetTournamentsParams,
  GetTournamentsResponse,
  CreateTournamentRequest,
  CreateTournamentResponse,
  JoinTournamentRequest,
  JoinTournamentResponse,
  InviteUserRequest,
  InviteUserResponse,
  GetTournamentUsersResponse,
  GetUserInvitationsResponse,
  RespondToInvitationRequest,
  RespondToInvitationAcceptResponse,
  RespondToInvitationDeclineResponse,
  GetTournamentDetailsResponse,
  GetTournamentPendingInvitationsResponse,
  GetUserRolesInTournamentResponse,
  CreateTournamentFormatBlocksRequest,
  CreateTournamentFormatBlocksResponse,
  UpdateTournamentFormatBlocksRequest,
  UpdateTournamentFormatBlocksResponse,
  GetTournamentFormatBlocksResponse,
  GetTournamentConstraintsResponse,
  CreateTournamentConstraintsRequest,
  CreateTournamentConstraintsResponse,
  UpdateTournamentConstraintsRequest,
  UpdateTournamentConstraintsResponse,
  UpdateTournamentRequest,
  UpdateTournamentResponse,
  GetTournamentDashboardResponse,
  DashboardMatchStats,
  DashboardFormatBlock,
  DashboardUpcomingMatch,
  DashboardRecentResult,
  DashboardTournament,
  DashboardWinners,
} from "./types";

/** Normalize dashboard API response: accept either camelCase or snake_case from backend */
function normalizeDashboardResponse(raw: any): GetTournamentDashboardResponse {
  const t = raw.tournament ?? {};
  const tournament: DashboardTournament = {
    id: t.id ?? "",
    name: t.name ?? "",
    code: t.code ?? "",
    description: t.description ?? undefined,
    startDate: t.startDate ?? t.start_date,
    endDate: t.endDate ?? t.end_date,
    location: t.location,
    cover: t.cover,
    status: t.status ?? "new",
    sportName: t.sportName ?? t.sport_name,
  };

  const ms = raw.matchStats ?? raw.match_stats;
  const matchStats: DashboardMatchStats = ms
    ? {
        total: ms.total ?? 0,
        completed: ms.completed ?? 0,
        ongoing: ms.ongoing ?? 0,
        scheduled: ms.scheduled ?? 0,
        draft: ms.draft ?? 0,
      }
    : { total: 0, completed: 0, ongoing: 0, scheduled: 0, draft: 0 };

  const blocks = raw.formatBlocks ?? raw.format_blocks ?? [];
  const formatBlocks: DashboardFormatBlock[] = Array.isArray(blocks)
    ? blocks.map((b: any) => ({
        id: b.id,
        formatType: b.formatType ?? b.format_type ?? "group",
        blockOrder: b.blockOrder ?? b.block_order ?? 0,
        matchCount: b.matchCount ?? b.match_count ?? 0,
        completedCount: b.completedCount ?? b.completed_count ?? 0,
        rules: b.rules ?? null,
      }))
    : [];

  const teamCount = raw.teamCount ?? raw.team_count ?? 0;

  const upcoming = raw.upcomingMatches ?? raw.upcoming_matches ?? [];
  const upcomingMatches: DashboardUpcomingMatch[] = Array.isArray(upcoming)
    ? upcoming.map((m: any) => ({
        id: m.id,
        startTime: m.startTime ?? m.start_time,
        team1: m.team1 ?? null,
        team2: m.team2 ?? null,
        venue: m.venue ?? null,
        status: m.status ?? "",
        formatType: m.formatType ?? m.format_type ?? "",
        round: m.round ?? null,
        group: m.group ?? null,
        bracket: m.bracket ?? null,
        isThirdPlaceMatch: m.isThirdPlaceMatch ?? m.is_third_place_match ?? false,
      }))
    : [];

  const nextRound = raw.nextRoundMatches ?? raw.next_round_matches ?? [];
  const nextRoundMatches: DashboardUpcomingMatch[] = Array.isArray(nextRound)
    ? nextRound.map((m: any) => ({
        id: m.id,
        startTime: m.startTime ?? m.start_time,
        team1: m.team1 ?? null,
        team2: m.team2 ?? null,
        venue: m.venue ?? null,
        status: m.status ?? "",
        formatType: m.formatType ?? m.format_type ?? "",
        round: m.round ?? null,
        group: m.group ?? null,
        bracket: m.bracket ?? null,
        isThirdPlaceMatch: m.isThirdPlaceMatch ?? m.is_third_place_match ?? false,
      }))
    : [];

  const recent = raw.recentResults ?? raw.recent_results ?? [];
  const recentResults: DashboardRecentResult[] = Array.isArray(recent)
    ? recent.map((m: any) => ({
        id: m.id,
        startTime: m.startTime ?? m.start_time,
        team1: m.team1 ?? null,
        team2: m.team2 ?? null,
        winner: m.winner ?? null,
        team1Score: m.team1Score ?? m.team1_score ?? 0,
        team2Score: m.team2Score ?? m.team2_score ?? 0,
        penalties: m.penalties ?? null,
        formatType: m.formatType ?? m.format_type ?? "",
        round: m.round ?? null,
        group: m.group ?? null,
        bracket: m.bracket ?? null,
        isThirdPlaceMatch: m.isThirdPlaceMatch ?? m.is_third_place_match ?? false,
      }))
    : [];

  const rawWinners = raw.winners ?? null;
  const winners: DashboardWinners | null = rawWinners
    ? {
        first: rawWinners.first,
        second: rawWinners.second,
        third: rawWinners.third ?? null,
      }
    : null;

  return {
    tournament,
    matchStats,
    formatBlocks,
    teamCount: Number(teamCount) || 0,
    playerCount: Number(raw.playerCount ?? raw.player_count ?? 0) || 0,
    upcomingFixturesCount: Number(raw.upcomingFixturesCount ?? raw.upcoming_fixtures_count ?? 0) || 0,
    matchesPlayedCount: Number(raw.matchesPlayedCount ?? raw.matches_played_count ?? 0) || 0,
    upcomingMatches,
    nextRoundMatches,
    recentResults,
    winners,
  };
}

// API Functions
export const tournamentsApi = {
  /**
   * Get a paginated list of tournaments
   * Superadmin users get all tournaments, while regular users only get tournaments
   * where they have "Tournament admin" role.
   */
  getTournaments: async (params?: GetTournamentsParams): Promise<GetTournamentsResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append("page", params.page.toString());
    }
    if (params?.limit) {
      queryParams.append("limit", params.limit.toString());
    }

    const queryString = queryParams.toString();
    const url = queryString ? `/tournaments?${queryString}` : "/tournaments";

    return api.get(url);
  },

  /**
   * Create a new tournament
   * Only superadmin users can create tournaments.
   * The creator automatically gets "Tournament admin" role for the created tournament.
   */
  createTournament: async (data: CreateTournamentRequest | FormData): Promise<CreateTournamentResponse> => {
    return api.post("/tournaments", data);
  },

  /**
   * Join a tournament using its unique code
   * Users can join tournaments by providing the tournament code.
   * The user will be registered as a participant in the tournament.
   */
  joinTournament: async (data: JoinTournamentRequest): Promise<JoinTournamentResponse> => {
    return api.post("/tournaments/join", data);
  },

  /**
   * Invite a user to a tournament by email and assign them a specific role
   * Only tournament admins or superadmins can invite users.
   */
  inviteUser: async (data: InviteUserRequest): Promise<InviteUserResponse> => {
    return api.post("/tournaments/invite", data);
  },

  /**
   * Get all users with their roles for a specific tournament
   * Only tournament admins or superadmins can view tournament users.
   */
  getTournamentUsers: async (tournamentId: string): Promise<GetTournamentUsersResponse> => {
    return api.get(`/tournaments/${tournamentId}/users`);
  },

  /**
   * Get all pending invitations for the current authenticated user
   * Returns only unresponded invitations (not accepted and not declined).
   */
  getUserInvitations: async (): Promise<GetUserInvitationsResponse> => {
    return api.get("/tournaments/invitations");
  },

  /**
   * Accept or decline a tournament invitation
   * When accepting, the role is automatically assigned to the user.
   */
  respondToInvitation: async (
    data: RespondToInvitationRequest,
  ): Promise<RespondToInvitationAcceptResponse | RespondToInvitationDeclineResponse> => {
    return api.post("/tournaments/invitations/respond", data);
  },

  /**
   * Get detailed information about a specific tournament by ID
   * Superadmin users can access any tournament, while regular users can access
   * tournaments where they have any role (Tournament admin, Registration manager, Observer, Schedule manager, etc.).
   */
  getTournamentDetails: async (tournamentId: string): Promise<GetTournamentDetailsResponse> => {
    return api.get(`/tournaments/${tournamentId}`);
  },

  /**
   * Update an existing tournament
   * Only tournament admins or superadmins can update tournaments.
   * Supports multipart/form-data for cover image upload.
   */
  updateTournament: async (
    tournamentId: string,
    data: UpdateTournamentRequest | FormData,
  ): Promise<UpdateTournamentResponse> => {
    return api.put(`/tournaments/${tournamentId}`, data);
  },

  /**
   * Get tournament dashboard data: overview statistics, format block progress,
   * team count, upcoming matches, and recent results.
   * Superadmin users can access any tournament; regular users must have any role in the tournament.
   */
  getTournamentDashboard: async (tournamentId: string): Promise<GetTournamentDashboardResponse> => {
    const res = await api.get(`/tournaments/${tournamentId}/dashboard`);
    const raw = res && typeof res === "object" && "data" in res ? (res as any).data : res;
    return normalizeDashboardResponse(raw ?? {});
  },

  /**
   * Get all pending invitations for a specific tournament
   * Only tournament admins or superadmins can view tournament invitations.
   */
  getTournamentPendingInvitations: async (tournamentId: string): Promise<GetTournamentPendingInvitationsResponse> => {
    return api.get(`/tournaments/${tournamentId}/invitations`);
  },

  /**
   * Get all roles for a specific user in a tournament
   * Users can view their own roles, or tournament admins/superadmins can view any user's roles.
   */
  getUserRolesInTournament: async (tournamentId: string, userId: string): Promise<GetUserRolesInTournamentResponse> => {
    return api.get(`/tournaments/${tournamentId}/users/${userId}/roles`);
  },

  /**
   * Create tournament format blocks with their associated rules
   * Only tournament admins can create format blocks.
   * Format blocks define the structure of a tournament (e.g., league, knockout, group stages).
   */
  createTournamentFormatBlocks: async (
    tournamentId: string,
    data: CreateTournamentFormatBlocksRequest,
  ): Promise<CreateTournamentFormatBlocksResponse> => {
    return api.post(`/tournaments/${tournamentId}/format-blocks`, data);
  },

  /**
   * Update tournament format blocks with their associated rules
   * Only tournament admins can update format blocks.
   * Format blocks not included in the request will be deleted.
   * Format blocks with an `id` field will be updated, while format blocks without an `id` will be created.
   */
  updateTournamentFormatBlocks: async (
    tournamentId: string,
    data: UpdateTournamentFormatBlocksRequest,
  ): Promise<UpdateTournamentFormatBlocksResponse> => {
    return api.put(`/tournaments/${tournamentId}/format-blocks`, data);
  },

  /**
   * Get all format blocks with their associated rules for a tournament
   * Format blocks are returned in order of their `blockOrder`.
   * All authenticated users can view format blocks for any tournament.
   */
  getTournamentFormatBlocks: async (tournamentId: string): Promise<GetTournamentFormatBlocksResponse> => {
    return api.get(`/tournaments/${tournamentId}/format-blocks`);
  },

  /**
   * Get tournament scheduling constraints
   * Returns the scheduling constraints for a tournament if they exist.
   * Returns 404 if constraints are not found for this tournament.
   */
  getTournamentConstraints: async (tournamentId: string): Promise<GetTournamentConstraintsResponse> => {
    return api.get(`/tournaments/${tournamentId}/constraints`);
  },

  /**
   * Create tournament scheduling constraints
   * Creates constraints for a tournament. Only one set of constraints can exist per tournament.
   * Returns 409 Conflict if constraints already exist (use update instead).
   */
  createTournamentConstraints: async (
    tournamentId: string,
    data: CreateTournamentConstraintsRequest,
  ): Promise<CreateTournamentConstraintsResponse> => {
    return api.post(`/tournaments/${tournamentId}/constraints`, data);
  },

  /**
   * Update tournament scheduling constraints
   * Updates existing constraints for a tournament.
   * Pass null to clear a field and use its default value.
   */
  updateTournamentConstraints: async (
    tournamentId: string,
    data: UpdateTournamentConstraintsRequest,
  ): Promise<UpdateTournamentConstraintsResponse> => {
    return api.put(`/tournaments/${tournamentId}/constraints`, data);
  },

  /**
   * Delete a tournament permanently
   * Only superadmins can delete tournaments.
   * Cascades and removes all related data (matches, teams, format blocks, etc.).
   */
  deleteTournament: async (tournamentId: string): Promise<void> => {
    return api.delete(`/tournaments/${tournamentId}`);
  },
};
